import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ghost from '../../api/ghost';
import { SinglePostSkeleton, StaticSinglePostSkeleton } from './SkeletonComponents';

// Blurhash effect placeholder component
const ImagePlaceholder = ({ className }) => (
  <div className={`animate-pulse bg-gradient-to-br from-neutral-800 to-neutral-900 ${className}`}></div>
);

// Improved ProgressiveImage component with direct loading
const ProgressiveImage = ({ src, alt, className, priority = false }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setLoaded(false);
    setError(false);
    
    if (!src) {
      setError(true);
      return;
    }

    // Check if image is already cached
    const img = new Image();
    
    const handleLoad = () => {
      setLoaded(true);
    };
    
    const handleError = () => {
      console.error(`Failed to load image: ${src}`);
      setError(true);
    };
    
    // Add event listeners
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    
    // Start loading the image
    img.src = src;
    
    // If image is complete already (cached), trigger load immediately
    if (img.complete) {
      handleLoad();
    }
    
    return () => {
      // Clean up listeners
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [src]);

  return (
    <div className={`relative overflow-hidden ${className || 'w-full h-full'}`}>
      {/* Placeholder displayed while loading */}
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-800 to-neutral-900 animate-pulse" />
      )}
      
      {/* Error fallback */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
          <span className="text-neutral-400">Image not available</span>
        </div>
      )}
      
      {/* The actual image */}
      {src && !error && (
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading={priority ? "eager" : "lazy"}
        />
      )}
    </div>
  );
};

function SinglePost() {
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const { slug } = useParams();
  const contentWrapperRef = useRef(null);

  // Function to extract first paragraph as excerpt if needed
  const extractExcerpt = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    const firstParagraph = div.querySelector('p');
    return firstParagraph ? firstParagraph.textContent : '';
  };

  // Process images in content function
  const processPostContent = (content) => {
    if (!content) return '';
    
    // Create a document fragment to parse HTML safely
    const doc = new DOMParser().parseFromString(content, 'text/html');
    
    // Process images for lazy loading and blur-up effect
    const images = doc.querySelectorAll('img');
    
    images.forEach((img, index) => {
      // Add loading eager to the first image, lazy to others
      img.setAttribute('loading', index === 0 ? 'eager' : 'lazy');
      
      // Add CSS class for fade-in effect
      img.classList.add('fade-in-image');
      
      // Track when image loads to remove placeholder
      const uniqueId = `post-img-${index}`;
      img.setAttribute('id', uniqueId);
      
      // Create and add a noscript fallback
      const noscript = document.createElement('noscript');
      noscript.innerHTML = img.outerHTML;
      img.parentNode.insertBefore(noscript, img.nextSibling);
    });
    
    // Add script to handle lazy image loading and placeholder removal
    const script = document.createElement('script');
    script.textContent = `
      document.addEventListener('DOMContentLoaded', function() {
        const postImages = document.querySelectorAll('.post-content img');
        postImages.forEach(img => {
          if (img.complete) {
            img.classList.add('loaded');
          } else {
            img.onload = function() {
              img.classList.add('loaded');
            }
          }
        });
      });
    `;
    doc.body.appendChild(script);
    
    return doc.body.innerHTML;
  };

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    
    // Use cached API for better performance
    ghost.posts
      .read({ slug, include: ['authors', 'tags'] })
      .then((post) => {
        // Process post content for lazy loading
        if (post.html) {
          post.html = processPostContent(post.html);
        }
        
        setPost(post);
        
        // Prefetch related content based on this post
        ghost.prefetchRelated(post);
        
        // Get related content by same author or tag
        const fetchRelated = async () => {
          try {
            let relatedResults = [];
            
            // Try to get posts with same primary tag
            if (post.tags && post.tags.length > 0) {
              const taggedPosts = await ghost.posts.browse({
                filter: `tag:${post.tags[0].slug}+id:-${post.id}`,
                limit: 3,
                include: ['authors']
              });
              
              if (taggedPosts && taggedPosts.length > 0) {
                relatedResults = taggedPosts;
              }
            }
            
            // If we didn't get enough related posts by tag, try by author
            if (relatedResults.length < 3 && post.primary_author) {
              const authorPosts = await ghost.posts.browse({
                filter: `primary_author:${post.primary_author.slug}+id:-${post.id}`,
                limit: 3 - relatedResults.length,
                include: ['authors']
              });
              
              if (authorPosts && authorPosts.length > 0) {
                relatedResults = [...relatedResults, ...authorPosts];
              }
            }
            
            setRelatedPosts(relatedResults);
          } catch (error) {
            console.error('Error fetching related posts:', error);
          }
        };
        
        fetchRelated();
        
        // If we have a primary author slug, fetch the full author data
        if (post.primary_author && post.primary_author.slug) {
          return ghost.authors.read({ slug: post.primary_author.slug });
        }
        return null;
      })
      .then((authorData) => {
        if (authorData) {
          setAuthor(authorData);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading post:', err);
        setError('Post not found or could not be loaded');
        setIsLoading(false);
      });
  }, [slug]);

  // Scroll to top when post changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Handle content links to open in new tab
  useEffect(() => {
    if (contentWrapperRef.current) {
      const externalLinks = contentWrapperRef.current.querySelectorAll('a[href^="http"]');
      externalLinks.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [post]);

  if (isLoading) return <SinglePostSkeleton />;
  
  if (error) return (
    <div className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-4xl mx-auto px-8 sm:px-8 md:px-12 py-16 sm:py-2 mt-20">
        <div className="mb-8 text-[#00CF9D] p-4 bg-neutral-900 border border-[#00CF9D]/20 rounded-md">
          <h3 className="font-mono font-bold mb-2">Oops! We couldn't load the content</h3>
          <p className="mb-4 text-neutral-300">{error}</p>
          <Link 
            to="/blog" 
            className="text-[#00CF9D] hover:underline font-mono text-sm flex items-center"
          >
            ← Back to blog
          </Link>
        </div>
        <StaticSinglePostSkeleton />
      </div>
    </div>
  );
  
  if (!post) return (
    <div className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-4xl mx-auto px-8 py-16 mt-20 text-neutral-300">
        <div className="mb-8 text-[#00CF9D] p-4 bg-neutral-900 border border-[#00CF9D]/20 rounded-md">
          <h3 className="font-mono font-bold mb-2">Post not found</h3>
          <p className="mb-4 text-neutral-300">The post you're looking for could not be found.</p>
          <Link 
            to="/blog" 
            className="text-[#00CF9D] hover:underline font-mono text-sm flex items-center"
          >
            ← Back to blog
          </Link>
        </div>
        <StaticSinglePostSkeleton />
      </div>
    </div>
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <main className="w-full overflow-x-hidden pt-16 bg-black">
      <article className="max-w-4xl mx-auto px-8 sm:px-8 md:px-12 py-16 sm:py-2 mt-20">
        <div className="mb-8">
          <Link 
            to="/blog" 
            className="text-[#00CF9D] hover:underline font-mono text-sm flex items-center mb-8"
          >
            ← Back to blog
          </Link>
          
          <motion.h1 
            className="text-3xl sm:text-4xl font-bold mb-4 font-mono text-[#DBDBDB]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {post.title}
          </motion.h1>
          
          <motion.div 
            className="mb-8 flex items-center text-neutral-400 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {post.published_at && (
              <time className="mr-2">{formatDate(post.published_at)}</time>
            )}
            
            {post.primary_author && (
              <Link 
                to={`/blog/author/${post.primary_author.slug}`} 
                className="text-[#00CF9D] hover:underline group flex items-center"
              >
                by {post.primary_author.name}
              </Link>
            )}
            
            <span className="mx-2">•</span>
            
            {post.reading_time && (
              <span>{post.reading_time} min read</span>
            )}
          </motion.div>
        </div>
        
        {post.feature_image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <ProgressiveImage 
              src={post.feature_image} 
              alt={post.feature_image_alt || post.title} 
              className="w-full h-auto max-h-[500px] object-cover rounded-lg mb-10"
              priority
            />
          </motion.div>
        )}
        
        {post.tags && post.tags.length > 0 && (
          <motion.div 
            className="mb-10 flex flex-wrap gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {post.tags.map(tag => (
              <Link
                to={`/blog?tag=${tag.slug}`}
                key={tag.id} 
                className="px-3 py-1 text-xs bg-neutral-800 text-neutral-300 rounded-full hover:bg-neutral-700 transition-colors"
              >
                {tag.name}
              </Link>
            ))}
          </motion.div>
        )}
        
        <motion.div 
          ref={contentWrapperRef}
          className="prose prose-invert max-w-none prose-headings:font-mono prose-headings:text-[#DBDBDB] prose-a:text-[#00CF9D] prose-blockquote:border-[#00CF9D] prose-pre:bg-neutral-900 prose-pre:border prose-pre:border-neutral-800 prose-iframe:w-full prose-iframe:aspect-video prose-iframe:rounded-lg prose-iframe:my-8 prose-iframe:mx-auto prose-iframe:shadow-lg prose-video:w-full prose-video:aspect-video prose-video:rounded-lg prose-video:my-8 prose-video:mx-auto prose-video:shadow-lg"
          dangerouslySetInnerHTML={{ __html: post.html }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        />
        
        {/* Author Section */}
        {author && (
          <motion.div 
            className="mt-16 pt-8 border-t border-neutral-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6 mb-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <Link to={`/blog/author/${author.slug}`} className="group">
                  <div className="relative">
                    {author.profile_image ? (
                      <img 
                        src={author.profile_image} 
                        alt={author.name}
                        className="w-16 h-16 rounded-full object-cover border border-[#00CF9D]/30 transition-all duration-300 group-hover:grayscale"
                        loading="lazy" 
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-neutral-800 flex items-center justify-center border border-[#00CF9D]/30 transition-all duration-300 group-hover:grayscale">
                        <span className="text-neutral-400 text-lg font-mono">
                          {author.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-xs text-[#00CF9D] font-medium opacity-0 group-hover:opacity-100">View profile</span>
                    </div>
                  </div>
                </Link>
                
                <div className="flex-1">
                  <Link to={`/blog/author/${author.slug}`} className="hover:underline">
                    <h3 className="text-xl font-mono font-bold text-[#00CF9D]">{author.name}</h3>
                  </Link>
                  
                  {author.location && (
                    <p className="text-sm text-neutral-400 mb-2">{author.location}</p>
                  )}
                  
                  {author.bio && (
                    <p className="text-neutral-300 text-sm mb-4">{author.bio}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-3">
                    {author.website && (
                      <a 
                        href={author.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#00CF9D] hover:underline flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="2" y1="12" x2="22" y2="12"></line>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                        Website
                      </a>
                    )}
                    
                    {author.twitter && (
                      <a 
                        href={`https://twitter.com/${author.twitter.replace('@', '')}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#00CF9D] hover:underline flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                        </svg>
                        Twitter
                      </a>
                    )}
                    
                    {author.facebook && (
                      <a 
                        href={`https://facebook.com/${author.facebook}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#00CF9D] hover:underline flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                        </svg>
                        Facebook
                      </a>
                    )}
                    
                    <Link 
                      to={`/blog/author/${author.slug}`}
                      className="text-sm text-[#00CF9D] hover:underline flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      </svg>
                      View all posts
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Related Posts Section */}
        {relatedPosts.length > 0 && (
          <motion.div
            className="mt-16 pt-8 border-t border-neutral-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <h3 className="text-xl font-mono font-bold text-[#DBDBDB] mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link 
                  to={`/blog/${relatedPost.slug}`} 
                  key={relatedPost.id}
                  className="bg-neutral-900 border border-neutral-800 rounded-md overflow-hidden hover:border-[#00CF9D] transition-colors duration-300 h-full block"
                >
                  <div className="relative h-40">
                    {relatedPost.feature_image ? (
                      <img 
                        src={relatedPost.feature_image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-neutral-800 flex items-center justify-center">
                        <span className="text-neutral-400 text-sm">No image available</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="text-base font-mono font-bold mb-2 text-[#DBDBDB] line-clamp-2">{relatedPost.title}</h4>
                    <div className="text-xs text-neutral-400 mb-2">
                      {relatedPost.published_at && (
                        <time>{new Date(relatedPost.published_at).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}</time>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
        
        <div className="mt-8 pt-8 border-t border-neutral-800">
          <Link 
            to="/blog" 
            className="inline-flex h-10 items-center justify-center rounded-md bg-[#00CF9D]/5 border border-[#00CF9D]/10 px-6 font-medium text-[#00CF9D] transition-colors hover:bg-[#00CF9D]/10 focus:outline-none focus:ring-2 focus:ring-[#00CF9D] focus:ring-offset-2 focus:ring-offset-neutral-900"
          >
            Back to all posts
          </Link>
        </div>
      </article>
    </main>
  );
}

export default SinglePost; 